import './OperationalRegister.css';
import { RegisterPhotoView } from '../../components/RegisterPhotoView.js';
import { RegisterPhoto, type RegisterPhotoDraft } from '../../components/RegisterPhoto.js';
// Pest control (lotta infestanti) — MOD 11 (T-246).
// Registrazione interventi/monitoraggi infestanti + stato scadenze.
// Sorgente: public.pest_control_logs. Export A4 in Oggi → Export Registri (MOD 11).

import { confirmDialog } from '../../lib/ui/dialogs.js';
import { toUserMessage } from '../../lib/errors.js';
import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonInput,
  IonItem, IonLabel, IonModal, IonPage,
  IonSpinner, IonTextarea, IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import { addOutline, warningOutline } from 'ionicons/icons';
import { useMemo, useState } from 'react';

import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useOfflineRegister, offlineRegisterStatus } from '../../lib/haccp/useOfflineRegister.js';

interface PclRow {
  foto_storage_path?: string | null;
  scheda_version?: number | null;
  mese_riferimento?: string | null;
  esca_consumata?: boolean | null;
  animali_morti?: boolean | null;
  operatore_nome?: string | null;
  id: string;
  data_intervento: string;
  tipo: string;
  esecutore_interno: boolean;
  esecutore_ditta: string | null;
  esecutore_nome: string | null;
  zone_controllate: string | null;
  esche_trappole: string | null;
  infestante_rilevato: string;
  rilievi: string | null;
  azioni: string | null;
  esito: string;
  prossima_scadenza: string | null;
  note: string | null;
}

const TIPO_OPTS: Array<[string, string]> = [
  ['monitoraggio_interno', 'Monitoraggio interno'],
  ['intervento_ditta', 'Intervento ditta'],
  ['ispezione', 'Ispezione'],
  ['derattizzazione', 'Derattizzazione'],
  ['disinfestazione', 'Disinfestazione'],
  ['disinfezione', 'Disinfezione'],
  ['allontanamento_volatili', 'Allontanamento volatili'],
  ['altro', 'Altro'],
];
const INFESTANTE_OPTS: Array<[string, string]> = [
  ['nessuno', 'Nessuno'],
  ['roditori', 'Roditori'],
  ['insetti_striscianti', 'Insetti striscianti'],
  ['insetti_volanti', 'Insetti volanti'],
  ['blatte', 'Blatte'],
  ['formiche', 'Formiche'],
  ['mosche', 'Mosche'],
  ['volatili', 'Volatili'],
  ['altro', 'Altro'],
];
const ESITO_OPTS: Array<[string, string]> = [
  ['nessuna_presenza', 'Nessuna presenza'],
  ['presenza_sotto_soglia', 'Presenza sotto soglia'],
  ['presenza_sopra_soglia', 'Presenza sopra soglia'],
  ['intervento_eseguito', 'Intervento eseguito'],
  ['altro', 'Altro'],
];

function todayIso(): string { const now = new Date(); return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10); }
function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return y && m && dd ? `${dd}/${m}/${y}` : String(d);
}
function statoScadenza(scad: string | null): { label: string; color: string } {
  if (!scad) return { label: 'Senza scadenza', color: '#94a3b8' };
  const days = Math.floor((new Date(scad + 'T00:00:00Z').getTime() - Date.now()) / 86400000);
  if (days < 0) return { label: 'Scaduto', color: '#dc2626' };
  if (days <= 30) return { label: `In scadenza (${days}gg)`, color: '#ea7300' };
  return { label: 'Pianificato', color: '#16a34a' };
}
function labelOf(opts: Array<[string, string]>, v: string): string {
  return opts.find(([k]) => k === v)?.[1] ?? v;
}

const EMPTY = {
  data_intervento: todayIso(), tipo: 'monitoraggio_interno', esecutore_interno: true,
  esecutore_ditta: '', esecutore_nome: '', zone_controllate: '', esche_trappole: '',
  infestante_rilevato: 'nessuno', rilievi: '', azioni: '', esito: 'nessuna_presenza',
  prossima_scadenza: '', note: '',
  mese_riferimento: todayIso().slice(0, 7), esca_consumata: null as boolean | null,
  animali_morti: null as boolean | null, _photo: null as RegisterPhotoDraft | null,
};

export function PestControlPage(): JSX.Element {
  const { profile } = useAuth();
  const canEdit = profile?.is_platform_owner === true
    || profile?.role === 'consulente' || profile?.role === 'ristoratore' || profile?.role === 'osa' || profile?.role === 'staff';

  const [formError, setFormError] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);
  const [toast, setToast] = useState<string | null>(null);

  const { register, form, setForm, open, start, close, save: saveRecord, saving, ready,
    hasDraft, hasDraftChanges, discard: discardRecord, draftStatus, draftError, retryDraft } = useOfflineRegister<PclRow, typeof EMPTY>(
    'pest_control_logs', EMPTY, 'data_intervento', canEdit,
  );
  const { data: rows = [], isLoading: loading, refetch: load } = register;

  const nextDue = useMemo(() => {
    const upcoming = rows
      .map((r) => r.prossima_scadenza)
      .filter((d): d is string => !!d)
      .map((d) => statoScadenza(d))
      .filter((s) => s.label === 'Scaduto' || s.label.startsWith('In scadenza'));
    return upcoming.length;
  }, [rows]);

  async function discard(): Promise<void> {
    const remove = async () => {
      try { await discardRecord(); }
      catch (error) { setToast('Impossibile scartare la bozza: ' + toUserMessage(error)); }
    };
    if (!hasDraftChanges) { await remove(); return; }
    // Dismiss the confirmation before closing its parent modal. Overlapping
    // dismissals can leave Ionic's root hidden/inert after both overlays close.
    if (await confirmDialog('I dati della bozza saranno eliminati da questo dispositivo.', {
      header: 'Scartare la bozza?', okText: 'Scarta bozza', danger: true,
    })) await remove();
  }

  async function save(): Promise<void> {
    if (!profile?.active_tenant_id) { setToast('Nessun ristorante attivo'); return; }
    if (photoBusy) return;
    setFormError(null);
    if (!form.data_intervento) { setFormError('Inserisci la data del controllo o del rapporto'); return; }
    if (form.esecutore_interno) {
      if (form.esca_consumata === null || form.animali_morti === null) { setFormError('Rispondi Sì o No alle due verifiche'); return; }
      if (!form.azioni.trim()) { setFormError('Descrivi i comportamenti adottati, anche se non sono necessari interventi'); return; }
    } else {
      if (!form.mese_riferimento) { setFormError('Indica il mese a cui si riferisce il rapporto'); return; }
      if (!form.esecutore_ditta.trim()) { setFormError('Indica la ditta che ha eseguito l’intervento'); return; }
      if (!form._photo) { setFormError('Allega la foto del rapporto firmato dalla ditta'); return; }
    }
    try {
      const saved = await saveRecord({
        scheda_version: 1, _photo: form._photo,
        mese_riferimento: form.esecutore_interno ? form.data_intervento.slice(0, 7) : form.mese_riferimento,
        esca_consumata: form.esecutore_interno ? form.esca_consumata : null,
        animali_morti: form.esecutore_interno ? form.animali_morti : null,
        operatore_nome: profile?.full_name ?? null,
        data_intervento: form.data_intervento,
        tipo: form.esecutore_interno ? 'monitoraggio_interno' : 'intervento_ditta',
        esecutore_interno: form.esecutore_interno,
        esecutore_ditta: form.esecutore_interno ? null : form.esecutore_ditta.trim(),
        esecutore_nome: form.esecutore_interno ? null : form.esecutore_nome.trim() || null,
        zone_controllate: form.esecutore_interno ? form.zone_controllate.trim() || null : null,
        esche_trappole: form.esche_trappole.trim() || null,
        infestante_rilevato: 'altro',
        rilievi: form.rilievi.trim() || null,
        azioni: form.esecutore_interno ? form.azioni.trim() || null : null,
        esito: form.esecutore_interno ? 'altro' : 'intervento_eseguito',
        prossima_scadenza: form.prossima_scadenza || null,
        note: form.note.trim() || null,
      });
      if (saved) setToast('Salvato sul dispositivo. In attesa di sincronizzazione.');
    } catch (error) {
      setToast('Salvataggio non riuscito: ' + toUserMessage(error));
    }
  }

  return (
    <IonPage className="operational-register">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>Pest control</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
          Le registrazioni si salvano sul dispositivo, anche senza rete, e si sincronizzano quando la connessione è disponibile.
        </p>
        {draftError && !open && <div role="alert"><p>{draftError}</p><IonButton onClick={retryDraft}>Riprova bozza</IonButton></div>}
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
          Scheda insetti e roditori: controlli del personale e rapporti mensili della ditta.
          La stampa del mese è in <b>Oggi → Export Registri</b>.
        </p>

        {nextDue > 0 && (
          <IonItem color="warning" lines="none" style={{ borderRadius: 10, marginBottom: 8 }}>
            <IonIcon icon={warningOutline} slot="start" />
            <IonLabel className="ion-text-wrap" style={{ fontSize: 13 }}>
              {nextDue} {nextDue === 1 ? 'intervento in scadenza o scaduto' : 'interventi in scadenza o scaduti'} — pianifica il prossimo controllo.
            </IonLabel>
          </IonItem>
        )}

        {canEdit && (
          <IonButton expand="block" disabled={!ready || saving} onClick={() => { start(); if (!hasDraft) setForm(f => ({ ...f, data_intervento: todayIso(), mese_riferimento: todayIso().slice(0, 7) })); }} style={{ '--border-radius': '12px', marginBottom: 12 } as React.CSSProperties}>
            <IonIcon icon={addOutline} slot="start" /> {hasDraft ? 'Riprendi bozza' : 'Registra controllo'}
          </IonButton>
        )}

        {register.isError && rows.length > 0 && (
          <div role="status"><p>Lettura non riuscita: sono mostrati gli ultimi dati caricati.</p><IonButton onClick={() => { void load(); }}>Riprova</IonButton></div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><IonSpinner /></div>
        ) : (register.isPending || register.isError) && rows.length === 0 ? (
          <div role="status" style={{ padding: 16 }}>
            <p>Impossibile leggere il registro sul dispositivo. Riprova.</p>
            <IonButton onClick={() => { void load(); }}>Riprova</IonButton>
          </div>
        ) : rows.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Nessun intervento disponibile su questo dispositivo.</p>
        ) : (
          rows.slice(0, visibleCount).map((r) => {
            const st = statoScadenza(r.prossima_scadenza);
            const presenza = r.infestante_rilevato !== 'nessuno';
            return (
              <div key={r.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                {offlineRegisterStatus(r) && <p role="status" style={{ fontSize: 12, color: r._syncStatus === 'rejected' ? '#b91c1c' : '#92400e', margin: '0 0 6px' }}>{offlineRegisterStatus(r)}</p>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <b style={{ fontSize: 15 }}>{r.scheda_version === 1 ? r.esecutore_interno ? 'Controllo del personale' : `Rapporto ditta · ${r.mese_riferimento?.split('-').reverse().join('/')}` : labelOf(TIPO_OPTS, r.tipo)}</b>
                  <span style={{ fontSize: 11, color: '#64748b' }}>{fmtDate(r.data_intervento)}</span>
                </div>
                {r.scheda_version === 1 ? <p style={{ fontSize: 14, margin: '8px 0' }}>
                  {r.esecutore_interno
                    ? `Esca consumata: ${r.esca_consumata === null ? 'Non rilevata' : r.esca_consumata ? 'Sì' : 'No'} · Animali morti: ${r.animali_morti === null ? 'Non rilevati' : r.animali_morti ? 'Sì' : 'No'}`
                    : `${r.esecutore_ditta ?? 'Ditta esterna'} · Rapporto firmato allegato`}
                </p> : <div style={{ fontSize: 12, color: presenza ? '#b45309' : '#16a34a', fontWeight: 600, marginTop: 4 }}>
                  {presenza ? `Presenza: ${labelOf(INFESTANTE_OPTS, r.infestante_rilevato)}` : 'Nessuna presenza'} · {labelOf(ESITO_OPTS, r.esito)}
                </div>}
                {r.operatore_nome && <p style={{ fontSize: 12 }}>Registrato da {r.operatore_nome}</p>}
                {r.foto_storage_path && profile?.active_tenant_id && <RegisterPhotoView key={r.foto_storage_path} tenantId={profile.active_tenant_id} path={r.foto_storage_path} />}
                {(r.zone_controllate || r.esche_trappole) && (
                  <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>{[r.zone_controllate, r.esche_trappole].filter(Boolean).join(' · ')}</div>
                )}
                {r.azioni && <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>Azioni: {r.azioni}</div>}
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  Prossimo controllo: <b>{fmtDate(r.prossima_scadenza)}</b>
                  {r.prossima_scadenza && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#fff', background: st.color, padding: '2px 8px', borderRadius: 999 }}>{st.label}</span>}
                </div>
              </div>
            );
          })
        )}

        {rows.length > visibleCount && <IonButton expand="block" fill="outline" onClick={() => setVisibleCount(n => n + 50)}>Mostra altri 50 controlli</IonButton>}
        <IonModal className="operational-register" isOpen={open} canDismiss={!saving && !photoBusy} onDidDismiss={close}>
          <IonHeader><IonToolbar>
            <IonTitle>Scheda insetti e roditori</IonTitle>
            <IonButtons slot="end">
              <IonButton color="danger" disabled={saving || photoBusy || !ready} onClick={() => { void discard(); }}>Scarta bozza</IonButton>
              <IonButton disabled={saving || photoBusy} onClick={close}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar></IonHeader>
          <IonContent className="ion-padding">
            <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
              {draftStatus}
            </p>
            {draftError && <div role="alert"><p>{draftError}</p><IonButton disabled={saving} onClick={retryDraft}>Riprova bozza</IonButton></div>}
            <fieldset disabled={saving || photoBusy} style={{ border: 0, padding: 0, margin: 0 }}>
              <legend style={{ fontWeight: 700, marginBottom: 8 }}>Chi ha eseguito il controllo?</legend>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <IonButton aria-pressed={form.esecutore_interno} fill={form.esecutore_interno ? 'solid' : 'outline'} disabled={saving || photoBusy}
                  onClick={() => setForm(f => ({ ...f, esecutore_interno: true }))}>Personale interno</IonButton>
                <IonButton aria-pressed={!form.esecutore_interno} fill={!form.esecutore_interno ? 'solid' : 'outline'} disabled={saving || photoBusy}
                  onClick={() => setForm(f => ({ ...f, esecutore_interno: false }))}>Ditta esterna · rapporto mensile</IonButton>
              </div>
              {!form.esecutore_interno && <>
                <p>Carica il rapporto firmato per il mese di riferimento. Le verifiche restano quelle documentate dalla ditta.</p>
                <IonItem><IonInput type="month" label="Mese di riferimento *" labelPlacement="stacked" value={form.mese_riferimento}
                  onIonInput={e => setForm(f => ({ ...f, mese_riferimento: String(e.detail.value ?? '') }))} /></IonItem>
                <IonItem><IonInput label="Ditta esterna *" labelPlacement="stacked" value={form.esecutore_ditta}
                  onIonInput={e => setForm(f => ({ ...f, esecutore_ditta: String(e.detail.value ?? '') }))} /></IonItem>
                <IonItem><IonInput label="Tecnico (facoltativo)" labelPlacement="stacked" value={form.esecutore_nome}
                  onIonInput={e => setForm(f => ({ ...f, esecutore_nome: String(e.detail.value ?? '') }))} /></IonItem>
              </>}
              <IonItem><IonInput type="date" label={form.esecutore_interno ? 'Data del controllo *' : 'Data dell’intervento sul rapporto *'} labelPlacement="stacked" value={form.data_intervento}
                onIonInput={e => setForm(f => ({ ...f, data_intervento: String(e.detail.value ?? '') }))} /></IonItem>
              {form.esecutore_interno && <section aria-label="Verifiche della scheda">
                {([['esca_consumata', 'Esca consumata?'], ['animali_morti', 'Animali morti?']] as const).map(([key, label]) => <fieldset key={key} style={{ border: '1px solid #cbd5e1', borderRadius: 12, margin: '12px 0', padding: 12 }}>
                  <legend>{label} *</legend>
                  <IonButton aria-pressed={form[key] === true} fill={form[key] === true ? 'solid' : 'outline'} disabled={saving || photoBusy} onClick={() => setForm(f => ({ ...f, [key]: true }))}>Sì</IonButton>
                  <IonButton aria-pressed={form[key] === false} fill={form[key] === false ? 'solid' : 'outline'} disabled={saving || photoBusy} onClick={() => setForm(f => ({ ...f, [key]: false }))}>No</IonButton>
                </fieldset>)}
                <IonItem><IonTextarea label="Comportamenti adottati *" labelPlacement="stacked" autoGrow placeholder="Descrivi cosa hai fatto o perché non sono necessari interventi"
                  value={form.azioni} onIonInput={e => setForm(f => ({ ...f, azioni: String(e.detail.value ?? '') }))} /></IonItem>
                <IonItem><IonInput label="Zone controllate (facoltativo)" labelPlacement="stacked" value={form.zone_controllate}
                  onIonInput={e => setForm(f => ({ ...f, zone_controllate: String(e.detail.value ?? '') }))} /></IonItem>
              </section>}
              <RegisterPhoto key={`${profile?.id}:${profile?.active_tenant_id}:${profile?.active_business_id}`} value={form._photo} disabled={saving}
                required={!form.esecutore_interno} onBusyChange={setPhotoBusy} onChange={photo => setForm(f => ({ ...f, _photo: photo }))} />
              <IonItem><IonInput type="date" label="Prossimo controllo (facoltativo)" labelPlacement="stacked" value={form.prossima_scadenza}
                onIonInput={e => setForm(f => ({ ...f, prossima_scadenza: String(e.detail.value ?? '') }))} /></IonItem>
              <IonItem><IonTextarea label="Note (facoltative)" labelPlacement="stacked" autoGrow value={form.note}
                onIonInput={e => setForm(f => ({ ...f, note: String(e.detail.value ?? '') }))} /></IonItem>
              <p style={{ fontSize: 13 }}>La registrazione conserva l’operatore attivo. Per la ditta, la firma è quella presente nel rapporto fotografato.</p>
            </fieldset>
            {formError && <p role="alert" style={{ color: "#b91c1c" }}>{formError}</p>}
            <IonButton expand="block" disabled={saving || photoBusy || !ready} onClick={() => { void save(); }} style={{ marginTop: 16, '--border-radius': '12px' } as React.CSSProperties}>
              {saving ? <IonSpinner name="dots" /> : form.esecutore_interno ? 'Salva controllo' : 'Salva rapporto mensile'}
            </IonButton>
          </IonContent>
        </IonModal>

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2600} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
